import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Save, RotateCcw, Plus, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

export const AdminGallery = () => {
  const [content, setContent] = useState({
    tagline: '',
    title: '',
    images: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/gallery`);
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/gallery`, { ...content, id: 'gallery' }, getAuthHeaders());
      toast.success('Gallery saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (index, field, value) => {
    const newImages = [...content.images];
    newImages[index] = { ...newImages[index], [field]: value };
    setContent(prev => ({ ...prev, images: newImages }));
  };

  const addImage = () => {
    setContent(prev => ({
      ...prev,
      images: [...prev.images, { src: '', alt: '', caption: '', rotation: 'rotate-0' }]
    }));
  };

  const removeImage = (index) => {
    setContent(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const rotationOptions = [
    { value: '-rotate-3', label: 'Tilt Left (-3°)' },
    { value: '-rotate-2', label: 'Tilt Left (-2°)' },
    { value: 'rotate-0', label: 'Straight' },
    { value: 'rotate-2', label: 'Tilt Right (2°)' },
    { value: 'rotate-3', label: 'Tilt Right (3°)' },
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="admin-gallery">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif">Performance Gallery</h1>
          <p className="text-muted-foreground">Manage the polaroid photo gallery</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchContent} disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-gallery-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-border p-6 space-y-4">
          <h2 className="font-semibold">Section Info</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Tagline</label>
              <input
                type="text"
                value={content.tagline}
                onChange={(e) => handleChange('tagline', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                value={content.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Polaroid Images</h2>
            <Button variant="outline" size="sm" onClick={addImage}>
              <Plus className="w-4 h-4 mr-1" /> Add Image
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {content.images.map((image, index) => (
              <div key={index} className="border border-border rounded-lg p-4 relative">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => removeImage(index)} 
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                
                {image.src && (
                  <div className="mb-4">
                    <img src={image.src} alt={image.alt} className="w-32 h-40 object-cover rounded border mx-auto" />
                  </div>
                )}
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Image URL</label>
                    <input
                      type="url"
                      value={image.src}
                      onChange={(e) => handleImageChange(index, 'src', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Caption (Handwritten)</label>
                    <input
                      type="text"
                      value={image.caption}
                      onChange={(e) => handleImageChange(index, 'caption', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                      placeholder="e.g., Jan - Grease"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Rotation</label>
                    <select
                      value={image.rotation}
                      onChange={(e) => handleImageChange(index, 'rotation', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                    >
                      {rotationOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Alt Text</label>
                    <input
                      type="text"
                      value={image.alt}
                      onChange={(e) => handleImageChange(index, 'alt', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-border rounded focus:border-primary focus:outline-none"
                      placeholder="Image description"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminGallery;
